import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell } from "recharts";
import { Brand100 } from "../utils/colors";
import "./PacingBar.scss";
import { Img } from "../Components";
import {
  calculatePositionOfBudgetLine,
  formatPacingBarValue,
  isTotalBar,
  scalePacingBarWidth,
} from "./pacingUtils";

const formatYValue = (value, marginLeft, numberOfSegments) => {
  return marginLeft === 1 && numberOfSegments.length > 1 ? `${value.trim()} Total` : value.trim();
};

const CustomYAxisTick = ({
  x,
  y,
  payload,
  marginLeft,
  isModal,
  numberOfSegments,
  longestText,
  total,
  currentSegment,
}) => {
  if (isModal) {
    return (
      <foreignObject x={x - 50} y={y - 10} width={200} height={100}>
        <div className="pacingBarText">Total</div>
      </foreignObject>
    );
  }

  const newValue = formatYValue(payload.value, marginLeft, numberOfSegments);
  const segmentsWithLogos = ["Channel", "Platform"];
  return (
    <foreignObject
      x={x - longestText - 24}
      y={marginLeft === 1 ? y - 15 : y - 10}
      width={140}
      height={125}
    >
      <div className="pacingBarYAxisContainer">
        <div className="pacingBarLogos">
          {segmentsWithLogos.includes(currentSegment) &&
            !total &&
            !(marginLeft === 1 && numberOfSegments.length > 1) && (
              <Img
                src={`https://cdn.blisspointmedia.com/assets/img/${`${currentSegment.toLowerCase()}s`}/${payload.value.replace(
                  /\s/g,
                  ""
                )}.png`}
                alt="brand health header"
                className="logoImage"
              />
            )}
        </div>
        <div className={`pacingBarText${marginLeft}`}>
          <div>{total ? "Total" : newValue}</div>
        </div>
      </div>
    </foreignObject>
  );
};

const bluesReversed = ["#D9F9FD", "#AEF3FC", "#2AE0F6"];

const getColor = (colors: string[], index: number) => {
  return colors[index];
};

let ctx;

export const measureText: any = text => {
  if (!ctx) {
    ctx = document.createElement("canvas").getContext("2d");
    ctx.font = "1rem 'Graphik";
  }

  return ctx.measureText(text).width;
};

interface PacingBarInfo {
  totalCleared: number;
  totalProjected: number;
  totalRemainder: number;
  segmentName: string;
  budget: number;
}

interface PacingBarProps {
  pacingData: PacingBarInfo;
  keys: string[];
  className?: string;
  colors?: string[];
  marginLeft: number;
  numberOfSegments: string[];
  longestText: number;
  isActual: boolean;
  isModal?: boolean;
  total?: boolean;
  currentSegment?: string;
  maxBudget?: number;
  goalName: string;
}

const CustomBarWithTarget: any = ({
  props,
  index,
  targetValue,
  isModal,
  marginLeft,
  keys,
  budgetLinePosition,
  goalName,
}) => {
  let { fill, x: propX, y, width, height } = props;
  const goalNameMetrics = ["ROAS", "CPX", "CPC", "CPM", "CTR"];

  const barName = goalName === "Budget" ? "Budget" : "Goal";
  const budgetText = goalNameMetrics.includes(goalName)
    ? `$${targetValue.toLocaleString("en-us", {
        maximumFractionDigits: 2,
      })} ${barName}`
    : `$${(targetValue / 1000).toLocaleString("en-us", {
        maximumFractionDigits: 2,
      })}k ${barName}`;

  if (width < 0) {
    width = 1;
  }
  const currentBar = keys[index];
  const { positionOfBudgetLine, xValue } = budgetLinePosition;
  const calculatedXPosition = xValue * width;

  let fontWeight = 600;
  let fontSize = 14;
  if (marginLeft >= 3) {
    fontWeight = 400;
    fontSize = 12;
  }

  let x = isModal ? propX - 15 : propX;
  return (
    <svg>
      <rect x={x} y={y} width={width} height={height} stroke="none" fill={fill} />
      {currentBar === positionOfBudgetLine && (
        <>
          <line
            x1={x + calculatedXPosition}
            x2={x + calculatedXPosition}
            y1={y}
            y2={y + height}
            stroke="#007F8E"
            strokeWidth={3}
          />
          <text
            x={x + calculatedXPosition + 3}
            y={y + height / 2}
            fill="#007F8E"
            dominantBaseline="middle"
            fontWeight={fontWeight}
            fontSize={fontSize}
          >
            {budgetText}
          </text>
        </>
      )}
    </svg>
  );
};

const GOAL_VALUE_MAP_FOR_BAR_LABELS: Record<string, string[]> = {
  Budget: [" Actual Spend", " Projected Total", "Remainder"],
  CPX: [" Actual", "", "Difference"],
  ROAS: [" Actual", "", "Difference"],
  CPC: [" Actual", "", "Difference"],
  CPM: [" Actual", "", "Difference"],
  CTR: [" Actual", "", "Difference"],
  "KPI Volume": [" Actual KPI Volume", " Projected KPI Volume", "Difference"],
  Revenue: [" Actual Revenue", " Projected Revenue", "Difference"],
};

const renderCustomizedLabel: any = (
  props,
  marginLeft,
  isModal,
  isActual,
  goalValue,
  key,
  pacingData,
  total,
  goalName
) => {
  const goalNameMetrics = ["ROAS", "CPX", "CPC", "CPM", "CTR"];
  let valueTag = "";
  let value = 0;
  const { x, y } = props;
  let yHeight = y;
  if (key === "totalRemainder") {
    return null;
  } else if (key === "totalProjected") {
    valueTag = GOAL_VALUE_MAP_FOR_BAR_LABELS[goalName][1];
    value = goalNameMetrics.includes(goalName)
      ? null
      : pacingData.totalProjected + pacingData.totalCleared;
    yHeight = total ? (isModal ? y + 50 - marginLeft * 3.5 : y + 74) : y + 50 - marginLeft * 3.5;
  } else if (key === "totalCleared") {
    valueTag = GOAL_VALUE_MAP_FOR_BAR_LABELS[goalName][0];
    value = pacingData.totalCleared;
    yHeight = y + 5;
  }

  const formattedValue = formatPacingBarValue(
    value,
    valueTag,
    goalValue,
    isModal,
    isActual,
    goalName
  );

  let fontWeight = 500;
  let fontSize = 14;
  let dominantBaseline = "hanging";
  if (marginLeft === 3) {
    fontWeight = 400;
    fontSize = 12;
    yHeight = key === "totalCleared" ? y + 5 : y + 25;
  } else if (marginLeft === 4) {
    fontWeight = 400;
    fontSize = 12;
    dominantBaseline = "auto";
    yHeight = key === "totalCleared" ? y - 2 : y + 27;
  }

  return (
    <text
      x={x + (isModal ? -5.08 : 9.92)}
      y={yHeight}
      fill={Brand100}
      textAnchor="start"
      dominantBaseline={dominantBaseline}
      fontSize={fontSize}
      fontFamily="Graphik"
      fontWeight={fontWeight}
    >
      {formattedValue}
    </text>
  );
};

const PacingBar: React.FC<PacingBarProps> = ({
  pacingData,
  keys,
  colors = bluesReversed,
  className,
  marginLeft,
  numberOfSegments,
  longestText,
  isModal = false,
  isActual,
  total = false,
  currentSegment,
  maxBudget,
  goalName,
}) => {
  const currentBudget = pacingData.budget;
  const maxTextWidth = measureText(pacingData.segmentName);
  let containerHeight = isTotalBar(total, marginLeft, numberOfSegments) ? 122 : 92;
  let barHeight = isTotalBar(total, marginLeft, numberOfSegments) ? 90 : 60;
  if (isModal) {
    containerHeight = 92;
    barHeight = 60;
  }
  if (marginLeft === 3) {
    containerHeight = 72;
    barHeight = 36;
  } else if (marginLeft === 4) {
    containerHeight = 48;
    barHeight = 16;
  }

  const budgetLinePosition = calculatePositionOfBudgetLine(pacingData);
  let editedBarWidget = scalePacingBarWidth(currentBudget, maxBudget || 0, marginLeft);
  const normalizedPacingData = [pacingData].map(item => {
    const total = item.totalCleared + item.totalProjected + item.totalRemainder;
    return {
      segmentName: item.segmentName,
      totalCleared: item.totalCleared / total,
      totalProjected: item.totalProjected / total,
      totalRemainder: item.totalRemainder / total,
    };
  });
  return (
    <ResponsiveContainer
      width={`${isModal ? 100 : editedBarWidget}%`}
      height={containerHeight}
      debounce={50}
    >
      <BarChart
        reverseStackOrder={true}
        data={normalizedPacingData}
        layout="vertical"
        margin={{
          left: isModal ? 0 : longestText,
          right: isModal ? 197 : maxTextWidth + 55,
        }}
        {...{
          overflow: "visible",
        }}
      >
        <XAxis hide type="number" domain={[0, 1]} />
        <YAxis
          dataKey={"segmentName"}
          type="category"
          axisLine={false}
          tickLine={false}
          tick={props => (
            <CustomYAxisTick
              {...props}
              marginLeft={marginLeft}
              isModal={isModal}
              numberOfSegments={numberOfSegments}
              longestText={longestText}
              total={total}
              currentSegment={currentSegment}
            />
          )}
          tickFormatter={value => formatYValue(value, marginLeft, numberOfSegments)}
        />
        {keys
          .slice()
          .reverse()
          .map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              barSize={barHeight}
              label={props =>
                renderCustomizedLabel(
                  props,
                  marginLeft,
                  isModal,
                  isActual,
                  currentBudget,
                  key,
                  pacingData,
                  isTotalBar(total, marginLeft, numberOfSegments),
                  goalName
                )
              }
              shape={props => (
                <CustomBarWithTarget
                  props={props}
                  index={index}
                  targetValue={currentBudget}
                  isModal={isModal}
                  marginLeft={marginLeft}
                  keys={keys}
                  budgetLinePosition={budgetLinePosition}
                  goalName={goalName}
                />
              )}
            >
              {[pacingData].map((entry, idx) => (
                <Cell key={`cell-${idx}`} fill={getColor(colors, index)} />
              ))}
            </Bar>
          ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default PacingBar;
